import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import { Box, Display, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import PageLayout from '@rover/react-lib/src/components/layouts/PageLayout';
import ResponsiveLayout from '@rover/react-lib/src/components/layouts/ResponsiveLayout';
import inject from '@rover/utilities/injector';

import {
  BACKGROUND_ACCENT_BRAND_2_COLORS_STYLES,
  StyledBox,
  StyledBulletedList,
} from '../brand2ColorStyles.constants';

import Ellipse from './images/ellipse.svg';

const { emergencyHotlineHours } = inject('Rover.context', '');
const ContentResponsiveLayout = styled(ResponsiveLayout)`
  margin-bottom: ${({ theme }) => theme.space['24x']};
  ${MQ.XS.toString()} {
    margin-bottom: ${({ theme }) => theme.space['16x']};
  }

  ${() => BACKGROUND_ACCENT_BRAND_2_COLORS_STYLES}
`;
const ResponsivePageLayout = styled(PageLayout)`
  margin-top: ${({ theme }) => theme.space['12x']};
  margin-bottom: ${({ theme }) => theme.space['4x']};
`;

const BeforeClaimSection = (): JSX.Element => {
  const { t } = useLingui();
  const { text, space } = useTheme();

  const listOfVetRelatedClaims = [
    {
      IconClass: Ellipse,
      title: t`All veterinary treatment notes and invoices from a board-certified practicing veterinarian.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Any photos, additional receipts, and other relevant documents.`,
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));

  const listOfDamageClaims = [
    {
      IconClass: Ellipse,
      title: t`Photos of the damage.`,
      content: '',
    },
    {
      IconClass: Ellipse,
      title: t`Receipts, original product packaging, warranty cards, or other proof of ownership to help establish fair market value.`,
      content: '',
    },
  ].map((item, index) => ({ ...item, key: index, hasBorder: false }));

  return (
    <ContentResponsiveLayout data-testid="before-claim-section">
      <ResponsivePageLayout>
        <Display size="500" mb="12x">
          <Trans>What to include with your claim</Trans>
        </Display>

        <Flex flexWrap="wrap">
          <StyledBox
            variant="card_default"
            flex={['100%', '1']}
            px={['4x', '12x']}
            py="12x"
            background="primary"
            minHeight="538px"
          >
            <Heading textColor="primary" size="300" textAlign="left" marginBottom="6x">
              <Trans>For vet care-related claims, include the following:</Trans>
            </Heading>

            <StyledBulletedList
              fontSize={text['200'].fontSize}
              fontWeight={text['200'].fontWeight}
              height={space['6x']}
              items={listOfVetRelatedClaims}
            />

            <Box padding="4x" background="secondary" mt="6x" width="100%">
              <Text fontWeight="semibold">
                <Trans>Note:</Trans>
              </Text>{' '}
              <Paragraph webProps={{ maxWidth: 'none', display: 'inline' }}>
                <Trans>
                  All documentation must be submitted within seven days of the injury. Once we've
                  received these documents, Rover’s claims team will review to determine
                  eligibility. Should we have additional questions, we may follow up with either
                  party.
                </Trans>
              </Paragraph>
            </Box>
          </StyledBox>

          <Box
            variant="card_default"
            flex={['100%', '1']}
            px={['4x', '12x']}
            py="12x"
            background="primary"
            minHeight="538px"
          >
            <Heading textColor="primary" size="300" textAlign="left" marginBottom="6x">
              <Trans>For property damage claims, include the following:</Trans>
            </Heading>

            <Paragraph textColor="primary">
              <Trans>All potentially relevant documentation, including</Trans>
            </Paragraph>

            <StyledBulletedList
              fontSize={text['200'].fontSize}
              fontWeight={text['200'].fontWeight}
              height={space['6x']}
              items={listOfDamageClaims}
            />

            <Box padding="4x" background="secondary" mt="6x" width="100%">
              <Text fontWeight="semibold">
                <Trans>Note:</Trans>{' '}
              </Text>
              <Paragraph webProps={{ maxWidth: 'none', display: 'inline' }}>
                <Trans>
                  Only damages to the pet owner’s dwelling or property are eligible. Generally,
                  damages caused by a pet owner’s pets to their own property is not covered. We may
                  require you to provide proof of claim submission with your homeowners insurance or
                  a documented police report to move forward with a claim.
                </Trans>
              </Paragraph>
            </Box>
          </Box>
        </Flex>

        <Paragraph
          textColor="primary"
          webProps={{ maxWidth: 'none' }}
          size="200"
          textAlign="left"
          marginTop="12x"
        >
          <Trans comment="{emergencyHotlineHours} is '24/7' for en-us, en-ca, fr-ca and empty string for other languages.">
            As pet parents ourselves, we know how emotional and challenging these situations can be,
            which is why we offer the protection of the Rover Guarantee. Our support and Trust &
            Safety teams are also available {emergencyHotlineHours} to help you through this
            process, answer questions, and simply be there for you and your pets.
          </Trans>
        </Paragraph>
      </ResponsivePageLayout>
    </ContentResponsiveLayout>
  );
};

export default BeforeClaimSection;
